import type { InsertConversationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertConversationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface InsertConversationInput {
  ParentId?: string | null;
  Content: string;
}

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }` so
 * `useComments.tinycomments_create` reads `.Id` regardless of which ran.
 */
export const useInsertConversation = createMutationHook({
  name: 'insertConversation',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.conversation.insert.mutationOptions({ onSuccess }),
  mapInput: (input: InsertConversationInput) => input,
  // A new conversation seeds its first comment — the editor re-reads the
  // comment list per conversation via `comment.commentsByConversationId`, so
  // invalidate that family.
  invalidate: (trpc) => [
    trpc.frontend.comment.commentsByConversationId.queryKey(),
  ],
  mapResponse: (result: { Id: string }): { Id: string } => ({
    Id: result.Id,
  }),
  graphqlDocument: InsertConversationDocument,
  // The Apollo `insertConversation` doc requires a non-null `ParentId`
  // (`uuid!`). Consumers guard on it before calling (see
  // `useComments.tinycomments_create`), so this narrows the optional wrapper
  // input to the doc's required variable without a cast.
  graphqlVariables: (input: InsertConversationInput) => {
    if (!input.ParentId) {
      throw new Error('Cannot insert conversation without a ParentId');
    }

    return {
      Content: input.Content,
      ParentId: input.ParentId,
    };
  },
  mapGraphqlResponse: (data: InsertConversationMutation): { Id: string } => {
    if (!data.insert_conversation_one) {
      throw new Error('Failed to insert conversation');
    }

    return { Id: data.insert_conversation_one.Id };
  },
});
